<template>
  <b-container fluid class="pt-3 px-5 flex-grow-1 d-flex flex-column">

    <!-- top row -->
    <b-row>
      <b-col cols="12" class="pt-1">
        <riskhub-vue-element-loading :active="loading" :is-full-screen="true" />
        <stat-panel v-show="!loading" class="mb-2" :data="getStatesPanel"></stat-panel>
      </b-col>
    </b-row>

    <!-- middle row -->
    <b-row>
      <b-col cols="12" lg="8" class="px-2 mb-3">
        <r-card
                title="Priorities"
                class="h-100"
        >
          <template #title>
            <div class="d-flex">
              <h4 class="card-title">Priorities</h4>
              <portal-target name="chart-select" class="ml-auto"></portal-target>
            </div>
          </template>
          <action-multi-chart />
          <template #footer>
            <b-card-footer><portal-target name="filters"></portal-target></b-card-footer>
          </template>
        </r-card>
      </b-col>
      <b-col cols="12" lg="4" class="px-2 mb-3">
        <r-card
            title="Status"
            class="h-100"
        >
          <action-count-hierarchy v-if="dataLoaded" :total="totalActions" />
        </r-card>
      </b-col>

    </b-row>

    <!-- bottom row -->
    <b-row>
      <b-col cols="12" lg="8" class="px-2 mb-3 mb-lg-0">
        <r-card
            :title="actionTypeBreakdown.label"
            :info="actionTypeBreakdown.info"
            class="h-100"
            title-class="mb-0"
        >
          <stat-box
              v-if="actionTypeBreakdown.value"
              :value="actionTypeBreakdown.value"
              size="small"
              :to="actionTypeBreakdown.to"
          />
          <breakdown-panel v-show="!loading" class="mt-2" :data="actionTypeBreakdown.breakdown" />
        </r-card>
      </b-col>
      <b-col cols="12" lg="4" class="px-2">
        <r-card
            title="Quicklinks"
            class="h-100"
        >
          <b-row class="d-flex h-100">
            <b-col class="align-self-center">
              <action-quicklinks />
            </b-col>
          </b-row>
        </r-card>
      </b-col>

    </b-row>
  </b-container>
</template>

<script>
import RCard from '@/components/Layout/RCard';
import StatPanel from '@/components/Dashboard/StatPanel';
import StatBox from '@/components/Dashboard/StatBox';
import ActionCountHierarchy from '@/components/Action/Dashboard/ActionCountHierarchy';
import BreakdownPanel from '@/components/Dashboard/BreakdownPanel';
import ActionQuicklinks from '@/components/Action/Dashboard/ActionQuicklinks';
import ActionMultiChart from '@/components/Action/Dashboard/ActionMultiChart';
import Action from '@/services/Action/Action';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import ActionStatus from '@/services/Action/ActionStatus';

export default {
  name: 'ActionsDashboard',
  components: {
    RCard,
    StatPanel,
    StatBox,
    ActionCountHierarchy,
    BreakdownPanel,
    ActionQuicklinks,
    ActionMultiChart,
  },
  data() {
    return {
      loading: false,
      dataLoading: false,
      dataLoaded: false,
      activeChart: 'bar',
      extendedCounts: null,
      statPanel: {
        group1: {
          label: 'Compliance',
          value: 0,
          info: 'This is the percentage of ‘Open’ actions that have a compliant status. ‘Upcoming’ actions are compliant and ‘Overdue’ actions are non-compliant.',
        },
        group2: {
          label: 'Open',
          value: 0,
          to: {
            name: 'actions',
          },
        },
        group3: {
          label: 'Overdue',
          value: 0,
          to: {
            name: 'actions',
            query: { overdue: 1 },
          },
          subgroups: [
            {
              label: '90+ DAYS',
              value: 0,
              to: {
                name: 'actions',
                query: { overdue: 7 },
              },
            },
            {
              label: '61-90 DAYS',
              value: 0,
              to: {
                name: 'actions',
                query: { overdue: 6 },
              },
            },
            {
              label: '31-60 DAYS',
              value: 0,
              to: {
                name: 'actions',
                query: { overdue: 5 },
              },
            },
            {
              label: '1-30 DAYS',
              value: 0,
              to: {
                name: 'actions',
                query: { overdue: 4 },
              },
            },
          ],
        },
        group4: {
          label: 'Upcoming',
          value: 0,
          to: {
            name: 'actions',
            query: { overdue: 2 },
          },
          subgroups: [
            {
              label: '1-30 DAYS',
              value: 0,
              to: {
                name: 'actions',
                query: { overdue: 8 },
              },
            },
            {
              label: '31-60 DAYS',
              value: 0,
              to: {
                name: 'actions',
                query: { overdue: 9 },
              },
            },
            {
              label: '61-90 DAYS',
              value: 0,
              to: {
                name: 'actions',
                query: { overdue: 10 },
              },
            },
            {
              label: '90+ DAYS',
              value: 0,
              to: {
                name: 'actions',
                query: { overdue: 11 },
              },
            },
          ],
        },
        upcomingTotal: 0,
      },
      actionTypeBreakdown: {
        label: 'Type',
        value: 0,
        to: {
          name: 'actions',
        },
        breakdown: [
          {
            label: 'CONTRACTOR',
            value: 0,
            icon: 'contractors',
            bgColor: '#34668C',
            to: {
              name: 'actions',
              query: { actionType: 'Contractor Action' },
            },
          },
          {
            label: 'SITE CHECK',
            value: 0,
            icon: 'sitecheck',
            bgColor: '#FFA600',
            to: {
              name: 'actions',
              query: { actionType: 'Site Check' },
            },
          },
          {
            label: 'STAFF',
            value: 0,
            icon: 'staff',
            bgColor: '#477A71',
            to: {
              name: 'actions',
              query: { actionType: 'Staff Action' },
            },
          },
          {
            label: 'INSPECTION, TEST, MAINTENANCE',
            value: 0,
            icon: 'inspection',
            bgColor: '#638BC2',
            to: {
              name: 'actions',
              query: { actionType: 'Inspection, Testing and Maintenance' },
            },
          },
          {
            label: 'POLICY & PROCEDURE',
            value: 0,
            icon: 'resources',
            bgColor: '#678DA9',
            to: {
              name: 'actions',
              query: { actionType: 'Policy/Procedure' },
            },
          },
          {
            label: 'OTHER',
            value: 0,
            icon: 'settings',
            bgColor: '#E87D5C',
            to: {
              name: 'actions',
              // TODO Handle aggregation of action types
              query: { hideActionType: ['Contractor Action', 'Site Check', 'Staff Action', 'Inspection, Testing and Maintenance', 'Policy/Procedure'] },
              params: { title: 'Other Action Type' },
            },
          },
        ],
      },
      dueDateStates: {},
    };
  },
  computed: {
    ...mapGetters([
      'complianceTypeId',
      'counts',
      'dueDateBy',
      'propertyTenure',
    ]),
    totalActions() {
      return ActionStatus.openStatuses().reduce((sum, status) => sum + this.counts[status], 0);
    },
    getStatesPanel() {
      const fraCompliance = this.compliancePercentage;

      if (this.dueDateBy === 'Work plan dates') {
        const statePanel = cloneDeep(this.statPanel);

        statePanel.group1.info = 'This is the percentage of \'Open\' actions that have a compliant status based on their work plan action due date, or, if they are not in a work plan, their FRA action due date. \'Upcoming\' actions are compliant and \'Overdue\' actions are non-compliant.';
        statePanel.group1.value = fraCompliance;

        statePanel.group3.to = {
          name: 'actions',
          query: { planduedate: 1 },
        };
        statePanel.group3.subgroups[0].to = {
          name: 'actions',
          query: { planduedate: 5 },
        };

        statePanel.group3.subgroups[1].to = {
          name: 'actions',
          query: { planduedate: 4 },
        };

        statePanel.group3.subgroups[2].to = {
          name: 'actions',
          query: { planduedate: 3 },
        };

        statePanel.group3.subgroups[3].to = {
          name: 'actions',
          query: { planduedate: 2 },
        };

        statePanel.group4.to = {
          name: 'actions',
          query: { planduedate: 6 },
        };

        statePanel.group4.subgroups[0].to = {
          name: 'actions',
          query: { planduedate: 7 },
        };

        statePanel.group4.subgroups[1].to = {
          name: 'actions',
          query: { planduedate: 8 },
        };

        statePanel.group4.subgroups[2].to = {
          name: 'actions',
          query: { planduedate: 9 },
        };

        statePanel.group4.subgroups[3].to = {
          name: 'actions',
          query: { planduedate: 10 },
        };

        return statePanel;
      }

      const statePanel = cloneDeep(this.statPanel);
      statePanel.group1.info = 'This is the percentage of \'Open\' actions that have a compliant status based on the FRA action due date. \'Upcoming\' actions are compliant and \'Overdue\' actions are non-compliant. ';
      statePanel.group1.value = fraCompliance;

      return statePanel;
    },
    compliancePercentage() {

      const rawCompliance = (this.statPanel.upcomingTotal / this.totalActions) * 100;
      const compliance = rawCompliance === 100 
        ? 100 
        : Math.min(Math.round(rawCompliance), 99);
      
      return Number.isFinite(compliance) ? compliance : 0;
    },
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    complianceTypeId() {
      window.location.reload();
    },
    propertyTenure(value) {
      if (value) {
        window.location.reload();
      }
    },
    dueDateBy() {
      this.refreshData();
    },
    counts() {
      this.refreshData();
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.dataLoading = true;

      try {
        await this.getCounts();
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: 'Something went wrong. Please try again later.',
          type: 'error',
        });
      } finally {
        this.loading = false;
        this.dataLoading = false;
      }
    },
    async getCounts() {
      const { data } = await Action.getExtendedCounts(
        this.complianceTypeId,
        this.propertyTenure,
      );
      this.extendedCounts = data;
      this.refreshData();
    },
    setStatPanelData(data) {
      if (!data || typeof data !== 'object') {
        return;
      }

      if (!Object.hasOwn(data, 'overdue') || !Object.hasOwn(data, 'upcoming')) {
        return;
      }

      const { overdue, upcoming } = data;

      // Total
      this.statPanel.group2.value = Number.isFinite(this.totalActions) ? this.totalActions : 0;

      // overdue
      const overdueTotal = overdue['90+ days'] + overdue['61-90 days'] + overdue['31-60 days'] + overdue['1-30 days'];
      this.statPanel.group3.value = overdueTotal;
      this.statPanel.group3.subgroups[0].value = overdue['90+ days'];
      this.statPanel.group3.subgroups[1].value = overdue['61-90 days'];
      this.statPanel.group3.subgroups[2].value = overdue['31-60 days'];
      this.statPanel.group3.subgroups[3].value = overdue['1-30 days'];

      // upcoming
      const upcomingTotal = upcoming['1-30 days'] + upcoming['31-60 days'] + upcoming['61-90 days'] + upcoming['90+ days'];
      this.statPanel.group4.value = upcomingTotal;
      this.statPanel.group4.subgroups[0].value = upcoming['1-30 days'];
      this.statPanel.group4.subgroups[1].value = upcoming['31-60 days'];
      this.statPanel.group4.subgroups[2].value = upcoming['61-90 days'];
      this.statPanel.group4.subgroups[3].value = upcoming['90+ days'];

      this.statPanel.upcomingTotal = upcomingTotal;

      this.statPanel.group1.label = this.dueDateBy === 'Work plan dates'
        ? 'Work plan compliance'
        : 'FRA compliance';
    },
    setBreakdownPanelData(data) {
      if (data.length === 0) {
        return;
      }

      const { actionType } = data;

      // Total
      this.actionTypeBreakdown.value = this.totalActions;

      // Breakdown
      this.actionTypeBreakdown.breakdown[0].value = actionType['contractor action'];
      this.actionTypeBreakdown.breakdown[1].value = actionType['site check'];
      this.actionTypeBreakdown.breakdown[2].value = actionType['staff action'];
      this.actionTypeBreakdown.breakdown[3].value = actionType['inspection testing and maintenance'];
      this.actionTypeBreakdown.breakdown[4].value = actionType['policy procedure'];
      this.actionTypeBreakdown.breakdown[5].value = this.getOtherActionTypeCount(actionType);
    },
    getOtherActionTypeCount(actionType) {
      const exclude = [
        'contractor action',
        'site check',
        'staff action',
        'inspection testing and maintenance',
        'policy procedure',
      ];

      let count = 0;
      for (const key in actionType) {
        if (!exclude.includes(key)) {
          count += actionType[key];
        }
      }

      return count;
    },
    refreshData() {
      if (!this.extendedCounts) {
        return;
      }

      if (Object.values(this.counts).includes(null)) {
        return;
      }

      this.dueDateStates = this.extendedCounts.data;
      this.setBreakdownPanelData(this.extendedCounts.data.fra_dates);
      const data = this.dueDateBy === 'Work plan dates'
        ? this.extendedCounts.data.workplan_dates
        : this.extendedCounts.data.fra_dates;
      this.setStatPanelData(data);
      this.dataLoaded = true;
    },
  },
};
</script>
